import { Button, Modal, Select, Spinner, Text, TextInput } from "north.js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

import { useUpdateClaimMutation } from "src/api/claims/updateClaim.generated";
import { Header, PageWrapper } from "src/shared/components";
import { useUserContext } from "src/shared/contexts";
import { HackerStage } from "src/shared/contexts/HackerContext/types";
import { useDeviceSize } from "src/shared/hooks";
import { mediaQueries } from "src/theme/deviceBreakpoints";

import { useRegisterBadgeCodeToEventAttendeeMutation } from "../graphql/registerBadgeCodeToEventAttendee.generated";
import { useRegisterBadgeUploadTokenMutation } from "../graphql/registerBadgeUploadToken.generated";

import {
  allocateBadgeId,
  APP_MODE_LABELS,
  attendeeDisplayName,
  attendeeReceivesBadge,
  badgeAppModeForRole,
  BadgeAppMode,
  buildBadgeIdentity,
  extractBadgeId,
  normalizedClaimStage,
  registrationStageIsEligible,
  RegistrationWaiverStatus,
} from "./badgeData";
import {
  BadgeAlreadyProvisionedError,
  BadgeInspection,
  BadgeOperationError,
  BadgeProvisioner,
} from "./badgeProvisioner";
import EmailQrScanner from "./EmailQrScanner";
import { useFindBadgeRegistrationAttendeeQuery } from "./graphql/findBadgeRegistrationAttendee.generated";
import { useUpsertRegistrationBadgeProfileMutation } from "./graphql/upsertRegistrationBadgeProfile.generated";
import { scanRegistrationActivity } from "./registrationActivityScan";

type StatusTone = "neutral" | "busy" | "success" | "warning" | "error";
type LookupMode = "email" | "badge";

type StatusMessage = {
  tone: StatusTone;
  title: string;
  detail?: string;
  requiresSpecialCases?: boolean;
};

type BadgeDraft = {
  legalName: string;
  badgeDisplayName: string;
  accountEmail: string;
  badgeId: string;
  networkingEmail: string;
  networkingPhone: string;
  networkingLinkedIn: string;
  networkingDiscord: string;
  networkingInstagram: string;
  networkingX: string;
};

type SaveableBadgeField = Exclude<
  keyof BadgeDraft,
  "legalName" | "accountEmail"
>;
type NetworkingBadgeField = Exclude<
  SaveableBadgeField,
  "badgeDisplayName" | "badgeId"
>;

type FieldSaveResult = {
  status: "idle" | "saving" | "saved" | "error";
  error?: string;
};

type RegistrationAttendee = {
  userId: number;
  eventAttendeeId: number | null;
  claimId: number | null;
  claimStage: string;
  role: string;
  badgeCode: string;
  accountEmail: string;
  badgeDisplayName: string;
  interests: string;
  networkingEmail: string;
  networkingPhone: string;
  networkingLinkedIn: string;
  networkingDiscord: string;
  networkingInstagram: string;
  networkingX: string;
  waiver: RegistrationWaiverStatus;
  registrationData: {
    preferredName: string;
    legalName: string;
    pronouns: string;
    accessibilityNeeds: string;
    shirtType: string;
    shirtSize: string;
    shirtAccommodations: string;
  };
};

type EditableValueProps = {
  label: string;
  value: string;
  savedValue: string;
  placeholder?: string;
  code?: boolean;
  saveResult?: FieldSaveResult;
  disabledReason?: string;
  onChange: (value: string) => void;
  onSave: () => void;
};

const EMPTY_BADGE_DRAFT: BadgeDraft = {
  legalName: "",
  badgeDisplayName: "",
  accountEmail: "",
  badgeId: "",
  networkingEmail: "",
  networkingPhone: "",
  networkingLinkedIn: "",
  networkingDiscord: "",
  networkingInstagram: "",
  networkingX: "",
};

const SAVEABLE_BADGE_FIELDS: SaveableBadgeField[] = [
  "badgeDisplayName",
  "badgeId",
  "networkingEmail",
  "networkingPhone",
  "networkingLinkedIn",
  "networkingDiscord",
  "networkingInstagram",
  "networkingX",
];

const NETWORKING_FIELD_SLUGS: Record<NetworkingBadgeField, string> = {
  networkingEmail: "networking_email",
  networkingPhone: "networking_phone_number",
  networkingLinkedIn: "networking_linkedin_link",
  networkingDiscord: "networking_discord_tag",
  networkingInstagram: "networking_instagram_link",
  networkingX: "networking_twitter_link",
};

const EditableValue: React.FC<EditableValueProps> = ({
  label,
  value,
  savedValue,
  placeholder = "Not provided",
  code = false,
  saveResult,
  disabledReason,
  onChange,
  onSave,
}) => {
  const dirty = value !== savedValue;
  const saving = saveResult?.status === "saving";
  const disabled = Boolean(disabledReason) || !dirty || saving;

  return (
    <>
      <EditableFieldRow>
        <EditableInput
          aria-label={label}
          value={value}
          placeholder={placeholder}
          $code={code}
          disabled={Boolean(disabledReason)}
          title={disabledReason}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !disabled) onSave();
          }}
        />
        <EditableSaveButton
          color="secondary"
          size="sm"
          disabled={disabled}
          title={disabledReason}
          onClick={onSave}
        >
          {saving
            ? "Saving…"
            : saveResult?.status === "error"
            ? "Retry"
            : dirty
            ? "Save"
            : "Saved"}
        </EditableSaveButton>
      </EditableFieldRow>
      {saveResult?.error && <FieldSaveError>{saveResult.error}</FieldSaveError>}
    </>
  );
};

const ReadOnlyValue: React.FC<{
  value: string;
  placeholder?: string;
  code?: boolean;
}> = ({ value, placeholder = "Not provided", code = false }) => (
  <ReadOnlyField $code={code}>{value || placeholder}</ReadOnlyField>
);

export const Registration: React.FC = () => {
  const isMediumOrSmaller = useDeviceSize("medium");

  if (isMediumOrSmaller) {
    return (
      <>
        <Header title="Badge registration" />
        <CompactPageWrapper fullWidth>
          <DesktopOnlyPanel role="status">
            <Text type="h2">Use a desktop for registration</Text>
            <MutedText>
              Badge registration is not supported on mobile or tablet devices.
              Open My Dashboard on a desktop or laptop computer to continue.
            </MutedText>
          </DesktopOnlyPanel>
        </CompactPageWrapper>
      </>
    );
  }

  return <DesktopRegistration />;
};

const DesktopRegistration: React.FC = () => {
  const { id: operatorUserId, isOrganizer } = useUserContext();
  const [email, setEmail] = useState("");
  const [badgeSearch, setBadgeSearch] = useState("");
  const [lookupMode, setLookupMode] = useState<LookupMode>("email");
  const [fastMode, setFastMode] = useState(true);
  const [showAttendee, setShowAttendee] = useState(false);
  const [attendeeData, setAttendeeData] = useState<RegistrationAttendee | null>(
    null
  );
  const [attendeeLoading, setAttendeeLoading] = useState(false);
  const [attendeeError, setAttendeeError] = useState<Error | null>(null);
  const [assignedBadgeId, setAssignedBadgeId] = useState("");
  const [badgeClaimId, setBadgeClaimId] = useState<number | null>(null);
  const [badgeDraft, setBadgeDraft] = useState<BadgeDraft>(EMPTY_BADGE_DRAFT);
  const [savedBadgeDraft, setSavedBadgeDraft] =
    useState<BadgeDraft>(EMPTY_BADGE_DRAFT);
  const [fieldSaveResults, setFieldSaveResults] = useState<
    Partial<Record<SaveableBadgeField, FieldSaveResult>>
  >({});
  // HackerAPI requires interests on extended-profile upserts, even when only
  // the badge display name changes. Preserve the attendee's existing value.
  const [badgeInterests, setBadgeInterests] = useState("");
  const [waiverStatus, setWaiverStatus] =
    useState<RegistrationWaiverStatus | null>(null);
  const [appMode, setAppMode] = useState<BadgeAppMode>("default");
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [attendeeStatus, setAttendeeStatus] = useState<StatusMessage | null>(
    null
  );
  const [connectionState, setConnectionState] = useState<
    "disconnected" | "connecting" | "connected"
  >("disconnected");
  const [operation, setOperation] = useState<
    | "connecting"
    | "inspecting"
    | "allocating"
    | "programming"
    | "resetting"
    | null
  >(null);
  const [inspection, setInspection] = useState<BadgeInspection | null>(null);
  const [serialLog, setSerialLog] = useState("");
  const [badgeConnectionError, setBadgeConnectionError] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showWaiverOverrideModal, setShowWaiverOverrideModal] = useState(false);
  const [waiverOverrideAcknowledged, setWaiverOverrideAcknowledged] =
    useState(false);
  const [showForceConfirmModal, setShowForceConfirmModal] = useState(false);
  const [forceConfirming, setForceConfirming] = useState(false);
  const [showBadgeOverwriteModal, setShowBadgeOverwriteModal] = useState(false);
  const [badgeToOverwriteId, setBadgeToOverwriteId] = useState("");
  const [showNoBadgeModal, setShowNoBadgeModal] = useState(false);
  const [showAlreadyCheckedInModal, setShowAlreadyCheckedInModal] =
    useState(false);
  const [showActiveErrorDetails, setShowActiveErrorDetails] = useState(false);
  const lastSearchedLookupRef = useRef<string | null>(null);

  const provisionerRef = useRef<BadgeProvisioner | null>(null);
  provisionerRef.current =
    provisionerRef.current ??
    new BadgeProvisioner({
      onLog: (text) => setSerialLog((current) => `${current}${text}`),
      onConnectionLost: (error) => {
        setConnectionState("disconnected");
        setBadgeConnectionError(false);
        setInspection(null);
        setOperation(null);
        setStatus({
          tone: "warning",
          title: "Badge disconnected",
          detail: error?.message || "Connect the next badge when ready.",
        });
      },
    });

  const [registerBadgeCode, badgeRegistration] =
    useRegisterBadgeCodeToEventAttendeeMutation({
      onCompleted: ({
        registerBadgeCodeToEventAttendee: registeredAttendee,
      }) => {
        void scanRegistrationActivity(
          badgeRegistration.client,
          registeredAttendee
        );
      },
    });
  const [registerBadgeUploadToken] = useRegisterBadgeUploadTokenMutation();
  const [updateClaim] = useUpdateClaimMutation();
  const [upsertRegistrationBadgeProfile] =
    useUpsertRegistrationBadgeProfileMutation();
  const { refetch: findBadgeRegistrationAttendee } =
    useFindBadgeRegistrationAttendeeQuery({
      skip: true,
      fetchPolicy: "network-only",
    });

  const attendee = showAttendee ? attendeeData : null;

  useEffect(() => {
    if (!attendee) return;
    const legalName = attendee.registrationData.legalName?.trim() || "";
    const badgeDisplayName =
      attendee.badgeDisplayName.trim() ||
      attendee.registrationData.preferredName?.trim() ||
      legalName;
    const badgeId = attendee.badgeCode || "";
    const seededDraft = {
      legalName,
      badgeDisplayName,
      accountEmail: attendee.accountEmail.trim(),
      badgeId,
      networkingEmail: attendee.networkingEmail.trim(),
      networkingPhone: attendee.networkingPhone.trim(),
      networkingLinkedIn: attendee.networkingLinkedIn.trim(),
      networkingDiscord: attendee.networkingDiscord.trim(),
      networkingInstagram: attendee.networkingInstagram.trim(),
      networkingX: attendee.networkingX.trim(),
    };
    setAssignedBadgeId(badgeId);
    setBadgeClaimId(attendee.claimId ?? null);
    setBadgeInterests(attendee.interests);
    setWaiverStatus(attendee.waiver);
    setBadgeDraft(seededDraft);
    setSavedBadgeDraft(seededDraft);
    setFieldSaveResults({});
    setAppMode(badgeAppModeForRole(attendee.role));
  }, [attendee]);

  useEffect(
    () => () => {
      void provisionerRef.current?.disconnect();
    },
    []
  );

  useEffect(() => {
    document.getElementById("registration-attendee-search")?.focus();
  }, []);

  const focusAttendeeSearch = useCallback((selectValue = false) => {
    window.setTimeout(() => {
      const input = document.getElementById(
        "registration-attendee-search"
      ) as HTMLInputElement | null;
      input?.focus();
      if (selectValue) input?.select();
    }, 0);
  }, []);

  const prepareNextLookup = useCallback(() => {
    setEmail("");
    setBadgeSearch("");
    lastSearchedLookupRef.current = null;
    focusAttendeeSearch();
  }, [focusAttendeeSearch]);

  const handleSearch = useCallback(
    async (rawValue?: string, requestedMode = lookupMode) => {
      const inputValue =
        rawValue ?? (requestedMode === "email" ? email : badgeSearch);
      const normalizedValue =
        requestedMode === "email"
          ? inputValue.trim().toLowerCase()
          : extractBadgeId(inputValue);
      if (!normalizedValue || attendeeLoading) return;

      const searchKey = `${requestedMode}:${normalizedValue}`;
      lastSearchedLookupRef.current = searchKey;
      setShowAttendee(false);
      setAttendeeData(null);
      setAttendeeLoading(true);
      setAttendeeError(null);
      setAssignedBadgeId("");
      setBadgeDraft(EMPTY_BADGE_DRAFT);
      setSavedBadgeDraft(EMPTY_BADGE_DRAFT);
      setFieldSaveResults({});
      setBadgeInterests("");
      setWaiverStatus(null);
      setWaiverOverrideAcknowledged(false);
      setShowWaiverOverrideModal(false);
      setShowForceConfirmModal(false);
      setForceConfirming(false);
      setAttendeeStatus(null);
      setShowAlreadyCheckedInModal(false);
      if (requestedMode === "badge") setBadgeSearch(normalizedValue);
      try {
        const { data } = await findBadgeRegistrationAttendee({
          email: requestedMode === "email" ? normalizedValue : null,
          badgeCode: requestedMode === "badge" ? normalizedValue : null,
        });
        if (lastSearchedLookupRef.current !== searchKey) return;

        const attendeeResult = data.findBadgeRegistrationAttendee;
        setAttendeeData({
          ...attendeeResult,
          eventAttendeeId: attendeeResult.eventAttendeeId ?? null,
          claimId: attendeeResult.claimId ?? null,
          waiver: {
            signed: attendeeResult.waiver.signed,
            signedAt: attendeeResult.waiver.signedAt ?? null,
            signer:
              attendeeResult.waiver.signer === "adult" ||
              attendeeResult.waiver.signer === "minor"
                ? attendeeResult.waiver.signer
                : null,
          },
        });
        setShowAttendee(true);
      } catch (error) {
        if (lastSearchedLookupRef.current !== searchKey) return;
        const normalizedError =
          error instanceof Error ? error : new Error(String(error));
        setAttendeeError(normalizedError);
      } finally {
        if (lastSearchedLookupRef.current === searchKey) {
          setAttendeeLoading(false);
          focusAttendeeSearch(true);
        }
      }
    },
    [
      attendeeLoading,
      badgeSearch,
      email,
      findBadgeRegistrationAttendee,
      focusAttendeeSearch,
      lookupMode,
    ]
  );

  useEffect(() => {
    if (!fastMode || lookupMode !== "email") return undefined;
    const normalizedEmail = email.trim().toLowerCase();
    const isValidEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalizedEmail);
    if (
      !isValidEmail ||
      `email:${normalizedEmail}` === lastSearchedLookupRef.current ||
      attendeeLoading
    ) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      void handleSearch(normalizedEmail);
    }, 100);
    return () => window.clearTimeout(timeout);
  }, [attendeeLoading, email, fastMode, handleSearch, lookupMode]);

  const busy = operation != null;
  const registeredBadgeIsVirtual = assignedBadgeId.startsWith("virtual-");
  const hasRegisteredPhysicalBadge = Boolean(
    assignedBadgeId && !registeredBadgeIsVirtual
  );
  const connectedBadgeId = inspection?.identity?.badge_id || "";
  const connectedBadgeOwnerName =
    attendee?.badgeCode === connectedBadgeId
      ? attendeeDisplayName(attendee)
      : inspection?.identity?.display_name || "Unknown attendee";
  const connectedBadgeAssociation = connectedBadgeId
    ? `${connectedBadgeOwnerName} (${connectedBadgeId})`
    : "";
  const receivesBadge = attendee ? attendeeReceivesBadge(attendee.role) : false;
  const hasEventAttendeeRecord = Boolean(attendee?.eventAttendeeId);
  const claimStage = normalizedClaimStage(attendee?.claimStage);
  const alreadyCheckedIn = claimStage === HackerStage.CHECKED_IN;
  const stageIsEligible = registrationStageIsEligible(claimStage);
  const canForceConfirm = isOrganizer && Boolean(attendee && attendee.claimId);
  const hasWaiverOverride = isOrganizer && waiverOverrideAcknowledged;
  const registrationBlockReasons = attendee
    ? [
        !stageIsEligible
          ? `Claim stage is ${
              attendee.claimStage || "unknown"
            }; only confirmed or checked-in attendees can be registered.`
          : null,
        stageIsEligible && !hasEventAttendeeRecord
          ? "No 2026 event attendee record was found for this account."
          : null,
        !waiverStatus?.signed && !hasWaiverOverride
          ? isOrganizer
            ? "No completed waiver was found. Acknowledgement is required to continue."
            : "No completed waiver was found. Ask an organizer to review the waiver requirement."
          : null,
      ].filter((reason): reason is string => reason != null)
    : [];
  const registrationBlocked = Boolean(
    attendee &&
      (!stageIsEligible ||
        !hasEventAttendeeRecord ||
        (!waiverStatus?.signed && !hasWaiverOverride))
  );
  const canProgram =
    connectionState === "connected" && !busy && !registrationBlocked;

  const hasUnsavedBadgeChanges = SAVEABLE_BADGE_FIELDS.some(
    (field) => badgeDraft[field] !== savedBadgeDraft[field]
  );
  const profileSaveDisabledReason = isOrganizer
    ? undefined
    : "Organizer access is required to save profile changes for another attendee.";
  const badgeIdSaveDisabledReason = registrationBlocked
    ? "Resolve the registration blockers before changing this badge ID."
    : !isOrganizer && operatorUserId === attendee?.userId
    ? "Volunteers cannot register or change their own badge ID."
    : undefined;

  const updateBadgeDraft = (field: keyof BadgeDraft, value: string) => {
    setBadgeDraft((current) => ({ ...current, [field]: value }));
    if (field !== "legalName" && field !== "accountEmail") {
      setFieldSaveResults((current) => ({
        ...current,
        [field]: { status: "idle" },
      }));
    }
  };

  const inspectConnectedBadge = async (allowDuringConnect = false) => {
    const provisioner = provisionerRef.current;
    if (!provisioner?.connected || (busy && !allowDuringConnect)) return;

    setOperation("inspecting");
    setStatus({ tone: "busy", title: "Reading badge data…" });
    try {
      const result = await provisioner.inspect();
      setBadgeConnectionError(false);
      setInspection(result);
      setStatus(
        result.provisioned
          ? {
              tone: "warning",
              title: "This badge is associated to",
              detail: `${
                result.identity?.display_name || "Unknown attendee"
              } (${result.identity?.badge_id || "Unknown badge ID"})`,
            }
          : {
              tone: "success",
              title: "Badge is connected and unassigned",
              detail: `BLE MAC ${result.mac}`,
            }
      );
    } catch (error) {
      if (error instanceof BadgeOperationError && !error.connectionRecovered) {
        setConnectionState("disconnected");
      }
      setBadgeConnectionError(
        error instanceof BadgeOperationError && error.connectionRecovered
      );
      setStatus({
        tone: "error",
        title: "Could not read badge data",
        detail: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setOperation(null);
    }
  };

  const lookupConnectedBadge = () => {
    const badgeId = inspection?.identity?.badge_id;
    if (!badgeId) {
      setStatus({
        tone: "neutral",
        title: "This badge is not assigned",
        detail: "There is no badge ID to look up.",
      });
      return;
    }

    setLookupMode("badge");
    setBadgeSearch(badgeId);
    void handleSearch(badgeId, "badge");
  };

  const connectBadge = async () => {
    const provisioner = provisionerRef.current;
    if (!provisioner || busy) return;

    setOperation("connecting");
    setConnectionState("connecting");
    setBadgeConnectionError(false);
    setStatus({
      tone: "busy",
      title: "Choose the badge serial port…",
      detail: "Select USB JTAG/serial debug unit (often shown as Espressif).",
    });
    try {
      await provisioner.connect();
      setConnectionState("connected");
      setOperation(null);
      await inspectConnectedBadge(true);
    } catch (error) {
      setConnectionState("disconnected");
      setBadgeConnectionError(false);
      setStatus({
        tone:
          error instanceof DOMException && error.name === "NotFoundError"
            ? "neutral"
            : "error",
        title:
          error instanceof DOMException && error.name === "NotFoundError"
            ? "Connection cancelled"
            : "Could not connect to the badge",
        detail:
          error instanceof Error
            ? error.message
            : "Check badge power and use a USB-C data cable.",
      });
    } finally {
      setOperation(null);
    }
  };

  const disconnectBadge = async () => {
    if (busy) return;
    await provisionerRef.current?.disconnect();
    setConnectionState("disconnected");
    setBadgeConnectionError(false);
    setInspection(null);
    setStatus({
      tone: "neutral",
      title: "Badge disconnected",
      detail: "Connect this badge or the next one when ready.",
    });
  };

  const allocateForAttendee = async (virtual: boolean): Promise<string> => {
    if (!attendee?.eventAttendeeId) throw new Error("Scan an attendee first");
    if (assignedBadgeId && virtual === registeredBadgeIsVirtual) {
      return assignedBadgeId;
    }

    const allocatedId = await allocateBadgeId({
      virtual,
      register: async (badgeId) => {
        await registerBadgeCode({
          variables: {
            event_attendee_id: attendee.eventAttendeeId!,
            user_id: attendee.userId,
            badge_code: badgeId,
          },
        });
      },
    });
    setAssignedBadgeId(allocatedId);
    updateBadgeDraft("badgeId", allocatedId);
    setSavedBadgeDraft((current) => ({
      ...current,
      badgeId: allocatedId,
    }));
    setFieldSaveResults((current) => ({
      ...current,
      badgeId: { status: "saved" },
    }));
    setAttendeeData((current) =>
      current
        ? {
            ...current,
            badgeCode: allocatedId,
            claimStage: HackerStage.CHECKED_IN,
          }
        : current
    );
    return allocatedId;
  };

  const registerSpecificBadgeId = async (badgeId: string): Promise<string> => {
    if (!attendee?.eventAttendeeId) throw new Error("Scan an attendee first");
    const normalizedBadgeId = extractBadgeId(badgeId).toLowerCase();
    await registerBadgeCode({
      variables: {
        event_attendee_id: attendee.eventAttendeeId,
        user_id: attendee.userId,
        badge_code: normalizedBadgeId,
      },
    });
    setAssignedBadgeId(normalizedBadgeId);
    updateBadgeDraft("badgeId", normalizedBadgeId);
    setSavedBadgeDraft((current) => ({
      ...current,
      badgeId: normalizedBadgeId,
    }));
    setFieldSaveResults((current) => ({
      ...current,
      badgeId: { status: "saved" },
    }));
    setAttendeeData((current) =>
      current
        ? {
            ...current,
            badgeCode: normalizedBadgeId,
            claimStage: HackerStage.CHECKED_IN,
          }
        : current
    );
    return normalizedBadgeId;
  };

  const saveBadgeField = async (field: SaveableBadgeField) => {
    if (!attendee) return;

    setFieldSaveResults((current) => ({
      ...current,
      [field]: { status: "saving" },
    }));

    try {
      let savedValue = badgeDraft[field].trim();
      if (field === "badgeId") {
        savedValue = extractBadgeId(savedValue);
        if (!savedValue) throw new Error("Badge ID cannot be empty.");
        savedValue = await registerSpecificBadgeId(savedValue);
        if (lookupMode === "badge") setBadgeSearch(savedValue);
      } else if (field === "badgeDisplayName") {
        if (!isOrganizer) {
          throw new Error(
            "Organizer access is required to save another attendee's badge name."
          );
        }
        const result = await upsertRegistrationBadgeProfile({
          variables: {
            userId: attendee.userId,
            badgeDisplayName: savedValue,
            interests: badgeInterests,
          },
        });
        if (result.errors?.length) throw result.errors[0];
      } else {
        if (!isOrganizer) {
          throw new Error(
            "Organizer access is required to save another attendee's RSVP networking information."
          );
        }
        if (!badgeClaimId) {
          throw new Error(
            "The attendee's application claim could not be found."
          );
        }
        const result = await updateClaim({
          variables: {
            updatedData: {
              id: badgeClaimId,
              answers: [
                {
                  slug: NETWORKING_FIELD_SLUGS[field],
                  answer: savedValue,
                },
              ],
            },
          },
        });
        if (result.errors?.length) throw result.errors[0];
      }

      setBadgeDraft((current) => ({ ...current, [field]: savedValue }));
      setSavedBadgeDraft((current) => ({
        ...current,
        [field]: savedValue,
      }));
      setFieldSaveResults((current) => ({
        ...current,
        [field]: { status: "saved" },
      }));
    } catch (error) {
      setFieldSaveResults((current) => ({
        ...current,
        [field]: {
          status: "error",
          error: error instanceof Error ? error.message : String(error),
        },
      }));
    }
  };

  const checkInWithoutBadge = async () => {
    if (!attendee || busy || registrationBlocked || hasUnsavedBadgeChanges)
      return;
    setOperation("allocating");
    setAttendeeStatus({
      tone: "busy",
      title: "Generating a virtual badge ID…",
      detail: "The attendee will be checked in without hardware.",
    });
    try {
      const editedBadgeId = badgeDraft.badgeId.trim();
      const badgeId = editedBadgeId.startsWith("virtual-")
        ? editedBadgeId === assignedBadgeId
          ? editedBadgeId
          : await registerSpecificBadgeId(editedBadgeId)
        : await allocateForAttendee(true);
      setAttendeeStatus({
        tone: "success",
        title: `${attendeeDisplayName(attendee)} is checked in`,
        detail: `No physical badge · ${badgeId}`,
      });
      prepareNextLookup();
    } catch (error) {
      setShowActiveErrorDetails(false);
      setAttendeeStatus({
        tone: "error",
        title: "Could not check in this attendee",
        detail: error instanceof Error ? error.message : String(error),
        requiresSpecialCases: true,
      });
    } finally {
      setOperation(null);
    }
  };

  const checkInAndProgramBadge = async (
    options: { allowOverwrite?: boolean } = {}
  ) => {
    const provisioner = provisionerRef.current;
    if (
      !attendee ||
      !attendee.eventAttendeeId ||
      !provisioner?.connected ||
      !canProgram ||
      registrationBlocked
    )
      return;
    const eventAttendeeId = attendee.eventAttendeeId;

    setAttendeeStatus(null);
    let badgeProgrammed = false;
    const editedBadgeId = badgeDraft.badgeId.trim();
    let badgeId =
      editedBadgeId && !editedBadgeId.startsWith("virtual-")
        ? editedBadgeId
        : "";
    try {
      if (!badgeId) {
        setOperation("allocating");
        setStatus({
          tone: "busy",
          title: "Allocating a unique badge ID…",
          detail: "My will retry up to five times if an ID is already taken.",
        });
        badgeId = await allocateForAttendee(false);
      } else if (badgeId !== assignedBadgeId) {
        setOperation("allocating");
        setStatus({
          tone: "busy",
          title: `Reserving ${badgeId}…`,
          detail: "Confirming that the edited badge ID is available.",
        });
        badgeId = await registerSpecificBadgeId(badgeId);
      }

      setOperation("programming");
      setStatus({
        tone: "busy",
        title: `Programming ${badgeId}…`,
        detail: "Keep the badge connected until verification finishes.",
      });
      const identity = buildBadgeIdentity({
        attendee: {
          eventAttendeeId,
          role: attendee.role,
          registrationData: {
            ...attendee.registrationData,
            legalName: badgeDraft.legalName,
          },
        },
        profile: {
          claimId: badgeClaimId,
          badgeDisplayName: badgeDraft.badgeDisplayName,
          networkingEmail: badgeDraft.networkingEmail,
          networkingPhone: badgeDraft.networkingPhone,
          networkingLinkedIn: badgeDraft.networkingLinkedIn,
          networkingDiscord: badgeDraft.networkingDiscord,
          networkingInstagram: badgeDraft.networkingInstagram,
          networkingX: badgeDraft.networkingX,
        },
        accountEmail: badgeDraft.accountEmail,
        badgeId,
      });
      const result = await provisioner.program(identity, appMode, {
        allowOverwrite: options.allowOverwrite,
      });
      badgeProgrammed = true;
      if (badgeClaimId) {
        const rawToken = Array.from(
          window.crypto.getRandomValues(new Uint8Array(32))
        )
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join("");
        await provisioner.writeUploadToken(rawToken);
        try {
          const saved = await registerBadgeUploadToken({
            variables: {
              event_attendee_id: eventAttendeeId,
              claim_id: badgeClaimId,
              badge_code: badgeId,
              badge_mac: result.mac,
              badge_secure_token_hash:
                "sha256:" +
                Array.from(
                  new Uint8Array(
                    await window.crypto.subtle.digest(
                      "SHA-256",
                      new TextEncoder().encode(rawToken)
                    )
                  )
                )
                  .map((byte) => byte.toString(16).padStart(2, "0"))
                  .join(""),
            },
          });
          if (!saved.data?.registerBadgeUploadToken || saved.errors?.length)
            throw new Error();
        } catch {
          throw new Error(
            "The badge was programmed, but its sync credential and MAC address were not saved. Keep it at the desk and retry programming before handing it out."
          );
        }
      }
      setBadgeConnectionError(false);
      setInspection({
        provisioned: true,
        mac: result.mac,
        appMode: result.appMode,
        identity: result.identity,
      });
      setStatus({
        tone: "success",
        title: `${identity.display_name}'s badge is ready`,
        detail: `${badgeId} · BLE MAC ${result.mac} · Safe to unplug`,
      });
      prepareNextLookup();
    } catch (error) {
      const isAlreadyProvisioned =
        error instanceof BadgeAlreadyProvisionedError;
      if (isAlreadyProvisioned) {
        setBadgeConnectionError(false);
        setBadgeToOverwriteId(error.badgeId);
        setShowBadgeOverwriteModal(true);
        setStatus({
          tone: "warning",
          title: "Overwrite confirmation needed",
          detail: error.message,
        });
        return;
      }
      if (error instanceof BadgeOperationError && !error.connectionRecovered) {
        setConnectionState("disconnected");
      }
      setBadgeConnectionError(
        !badgeProgrammed &&
          provisioner.connected &&
          (!(error instanceof BadgeOperationError) || error.connectionRecovered)
      );
      setShowActiveErrorDetails(false);
      setStatus({
        tone: "error",
        title: badgeProgrammed
          ? "Badge programmed, but setup did not finish"
          : badgeId
          ? "Badge ID assigned, but programming failed"
          : "Could not allocate a badge ID",
        detail: error instanceof Error ? error.message : String(error),
        requiresSpecialCases: true,
      });
    } finally {
      setOperation(null);
    }
  };

  const factoryResetBadge = async () => {
    const provisioner = provisionerRef.current;
    if (!provisioner?.connected || busy) return;
    const confirmed = window.confirm(
      `Factory-reset this badge?${
        connectedBadgeAssociation
          ? `\n\nThis badge is associated to ${connectedBadgeAssociation}.`
          : ""
      }\n\nThis erases its provisioned identity, contacts, settings, and installed apps. Its wallet key is preserved. This cannot be undone.`
    );
    if (!confirmed) return;

    setOperation("resetting");
    setStatus({
      tone: "busy",
      title: "Factory-resetting badge…",
      detail: "Keep the cable connected until reset is confirmed.",
    });
    try {
      await provisioner.factoryReset();
      setConnectionState("disconnected");
      setBadgeConnectionError(false);
      setInspection(null);
      setStatus({
        tone: "success",
        title: "Badge factory reset complete",
        detail:
          "Identity, contacts, settings, and installed apps were cleared. Unplug the badge, then plug it back in before continuing.",
      });
    } catch (error) {
      if (error instanceof BadgeOperationError && !error.connectionRecovered) {
        setConnectionState("disconnected");
      }
      setBadgeConnectionError(
        provisioner.connected &&
          error instanceof BadgeOperationError &&
          error.connectionRecovered
      );
      setStatus({
        tone: "error",
        title: "Factory reset incomplete",
        detail: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setOperation(null);
    }
  };

  const scanNextAttendee = () => {
    setEmail("");
    setBadgeSearch("");
    lastSearchedLookupRef.current = null;
    setShowAttendee(false);
    setAttendeeData(null);
    setAttendeeLoading(false);
    setAttendeeError(null);
    setAssignedBadgeId("");
    setBadgeClaimId(null);
    setBadgeDraft(EMPTY_BADGE_DRAFT);
    setSavedBadgeDraft(EMPTY_BADGE_DRAFT);
    setFieldSaveResults({});
    setBadgeInterests("");
    setWaiverStatus(null);
    setWaiverOverrideAcknowledged(false);
    setAttendeeStatus(null);
    setShowOverrideModal(false);
    setShowWaiverOverrideModal(false);
    setShowBadgeOverwriteModal(false);
    setBadgeToOverwriteId("");
    setShowNoBadgeModal(false);
    setShowAlreadyCheckedInModal(false);
    setShowActiveErrorDetails(false);
    setStatus(null);
    window.setTimeout(
      () => document.getElementById("registration-attendee-search")?.focus(),
      0
    );
  };

  const switchLookupMode = (mode: "email" | "badge") => {
    if (mode === lookupMode) return;
    scanNextAttendee();
    setLookupMode(mode);
  };

  const forceConfirmAcceptedClaim = async () => {
    const claimId = attendee && attendee.claimId;
    if (!claimId || !canForceConfirm || forceConfirming) return;

    setForceConfirming(true);
    setAttendeeStatus({
      tone: "busy",
      title: "Confirming application...",
      detail: "Updating the claim stage to confirmed.",
    });
    try {
      const result = await updateClaim({
        variables: {
          updatedData: {
            id: claimId,
            stage_slug: HackerStage.CONFIRMED,
          },
        },
      });
      if (result.errors && result.errors.length) throw result.errors[0];

      const updatedClaim = result.data && result.data.updateClaim;
      const confirmedStage =
        (updatedClaim && updatedClaim.stage && updatedClaim.stage.slug) ||
        HackerStage.CONFIRMED;
      setAttendeeData((current) => {
        if (!current) return current;
        return {
          ...current,
          claimStage: confirmedStage,
        };
      });
      setShowForceConfirmModal(false);
      setAttendeeStatus({
        tone: "success",
        title: "Application confirmed",
        detail: "RSVP was overridden. Registration can continue.",
      });
    } catch (error) {
      setAttendeeStatus({
        tone: "error",
        title: "Could not confirm application",
        detail: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setForceConfirming(false);
    }
  };

  const attendeeName = attendee
    ? attendeeDisplayName(attendee)
    : "this attendee";
  const physicalActionLabel = hasRegisteredPhysicalBadge
    ? "Program assigned badge"
    : "Check in & program badge";
  const programDataUnavailable =
    busy || hasUnsavedBadgeChanges || registrationBlocked;
  const activeError =
    status?.tone === "error" && status.requiresSpecialCases
      ? status
      : attendeeStatus?.tone === "error" && attendeeStatus.requiresSpecialCases
      ? attendeeStatus
      : null;

  const requestBadgeProgramming = (confirmedAlreadyCheckedIn = false) => {
    if (registrationBlocked || hasUnsavedBadgeChanges) return;
    if (alreadyCheckedIn && !confirmedAlreadyCheckedIn) {
      setShowAlreadyCheckedInModal(true);
      return;
    }
    if (connectionState !== "connected") {
      setShowNoBadgeModal(true);
      return;
    }
    const existingBadgeId = inspection?.identity?.badge_id || "";
    const intendedBadgeId = badgeDraft.badgeId.trim() || assignedBadgeId;
    if (inspection?.provisioned && existingBadgeId !== intendedBadgeId) {
      setBadgeToOverwriteId(existingBadgeId || "an unknown badge ID");
      setShowBadgeOverwriteModal(true);
      return;
    }
    void checkInAndProgramBadge();
  };

  const dismissActiveError = () => {
    if (status?.requiresSpecialCases) setStatus(null);
    if (attendeeStatus?.requiresSpecialCases) setAttendeeStatus(null);
    setShowActiveErrorDetails(false);
  };

  return (
    <>
      <Header
        title="Badge registration"
        subtitle="Check in attendees and provision Hack the North 2026 badges"
      />
      <CompactPageWrapper fullWidth>
        <Workspace>
          <Panel>
            <PanelHeading>
              <StepNumber>1</StepNumber>
              <HeadingCopy>
                <Text type="h2">Find attendee</Text>
                <MutedText>
                  {lookupMode === "email"
                    ? "Scan or enter the attendee's account email."
                    : "Enter a badge ID or scan its QR URL."}
                </MutedText>
              </HeadingCopy>
              {lookupMode === "email" && (
                <FastModeSwitch
                  type="button"
                  role="switch"
                  aria-checked={fastMode}
                  onClick={() => setFastMode((enabled) => !enabled)}
                >
                  <SwitchTrack $enabled={fastMode}>
                    <SwitchThumb $enabled={fastMode} />
                  </SwitchTrack>
                  Fast mode {fastMode ? "on" : "off"}
                </FastModeSwitch>
              )}
            </PanelHeading>
            <LookupModeToggle>
              <Button
                color={lookupMode === "email" ? "primary" : "secondary"}
                size="sm"
                aria-pressed={lookupMode === "email"}
                onClick={() => switchLookupMode("email")}
              >
                Look up by email
              </Button>
              <Button
                color={lookupMode === "badge" ? "primary" : "secondary"}
                size="sm"
                aria-pressed={lookupMode === "badge"}
                onClick={() => switchLookupMode("badge")}
              >
                Look up by badge
              </Button>
            </LookupModeToggle>
            <SearchRow>
              <FullWidthInput
                id="registration-attendee-search"
                size="md"
                type={lookupMode === "email" ? "email" : "text"}
                placeholder={
                  lookupMode === "email"
                    ? "person@example.com"
                    : "Badge ID or hackthenorth.com/qr/2026/…"
                }
                value={lookupMode === "email" ? email : badgeSearch}
                error={attendeeError?.message}
                trailingIcon={
                  lookupMode === "email" ? (
                    <EmailQrScanner
                      disabled={attendeeLoading}
                      onScan={setEmail}
                      onClose={() => focusAttendeeSearch()}
                    />
                  ) : undefined
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (lookupMode === "email") setEmail(event.target.value);
                  else setBadgeSearch(event.target.value);
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === "Enter") void handleSearch();
                }}
              />
              <SearchButton
                color="primary"
                size="lg"
                disabled={
                  !(lookupMode === "email" ? email : badgeSearch).trim() ||
                  attendeeLoading
                }
                onClick={() => void handleSearch()}
              >
                {attendeeLoading
                  ? "Searching…"
                  : lookupMode === "email"
                  ? "Find attendee"
                  : "Find badge owner"}
              </SearchButton>
            </SearchRow>
            {fastMode && lookupMode === "email" && (
              <FastModeHint>
                Scanner input is looked up automatically as soon as a complete
                email is detected.
              </FastModeHint>
            )}

            {attendee && (
              <AttendeeSection>
                <AttendeeHeader>
                  <div>
                    <AttendeeName>
                      {badgeDraft.legalName || attendeeName}
                    </AttendeeName>
                    <RoleLine>
                      <RolePill>{attendee.role}</RolePill>
                      <WaiverPill
                        $signed={waiverStatus?.signed === true}
                        $unknown={waiverStatus == null}
                      >
                        {waiverStatus?.signed
                          ? `Waiver signed · ${
                              waiverStatus.signer === "minor"
                                ? "Under 18"
                                : "18+"
                            }${
                              waiverStatus.signedAt
                                ? ` · ${waiverStatus.signedAt.slice(0, 10)}`
                                : ""
                            }`
                          : hasWaiverOverride
                          ? "Waiver override armed"
                          : waiverStatus
                          ? "Waiver not signed"
                          : "Waiver unavailable"}
                      </WaiverPill>
                      <StagePill>Claim stage: {attendee.claimStage}</StagePill>
                      <RegistrationStatusPill
                        $tone={
                          alreadyCheckedIn
                            ? "warning"
                            : registrationBlocked
                            ? "danger"
                            : "success"
                        }
                      >
                        {alreadyCheckedIn
                          ? "Already checked in"
                          : registrationBlocked
                          ? "Registration blocked"
                          : "Ready to check in"}
                      </RegistrationStatusPill>
                    </RoleLine>
                  </div>
                </AttendeeHeader>

                {registrationBlockReasons.length > 0 && (
                  <RegistrationBlockWarning role="alert">
                    <strong>Do not register this attendee</strong>
                    <ul>
                      {registrationBlockReasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                    Send this attendee to Special Cases for help.
                  </RegistrationBlockWarning>
                )}

                {hasWaiverOverride && (
                  <AlreadyCheckedInWarning role="alert">
                    <strong>Waiver warning acknowledged.</strong> This allows
                    this registration attempt only and does not mark the
                    attendee&apos;s waiver as signed.
                  </AlreadyCheckedInWarning>
                )}

                {alreadyCheckedIn && (
                  <AlreadyCheckedInWarning role="status">
                    <strong>This attendee is already checked in.</strong> You
                    can reprogram their badge, but you will be asked to confirm
                    first.
                  </AlreadyCheckedInWarning>
                )}

                {!receivesBadge && (
                  <RoleNotice>
                    <strong>{attendee.role}</strong> attendees are normally
                    checked in without a hacker badge.
                  </RoleNotice>
                )}

                {attendeeStatus && !attendeeStatus.requiresSpecialCases && (
                  <AttendeeStatusBanner
                    tone={attendeeStatus.tone}
                    role="status"
                    aria-live="polite"
                  >
                    <StatusCopy>
                      <StatusTitle>{attendeeStatus.title}</StatusTitle>
                      {attendeeStatus.detail && (
                        <StatusDetail>{attendeeStatus.detail}</StatusDetail>
                      )}
                    </StatusCopy>
                    {operation === "allocating" && <Spinner />}
                  </AttendeeStatusBanner>
                )}

                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>Legal name</InfoLabel>
                    <ReadOnlyValue value={badgeDraft.legalName} />
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Badge name</InfoLabel>
                    <EditableValue
                      label="Badge name"
                      value={badgeDraft.badgeDisplayName}
                      savedValue={savedBadgeDraft.badgeDisplayName}
                      saveResult={fieldSaveResults.badgeDisplayName}
                      disabledReason={profileSaveDisabledReason}
                      onChange={(value) =>
                        updateBadgeDraft("badgeDisplayName", value)
                      }
                      onSave={() => void saveBadgeField("badgeDisplayName")}
                    />
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Account email</InfoLabel>
                    <ReadOnlyValue value={badgeDraft.accountEmail} />
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Badge ID</InfoLabel>
                    <EditableValue
                      label="Badge ID"
                      code
                      value={badgeDraft.badgeId}
                      savedValue={savedBadgeDraft.badgeId}
                      placeholder="Generated when checked in"
                      saveResult={fieldSaveResults.badgeId}
                      disabledReason={badgeIdSaveDisabledReason}
                      onChange={(value) => updateBadgeDraft("badgeId", value)}
                      onSave={() => void saveBadgeField("badgeId")}
                    />
                  </InfoItem>
                </InfoGrid>

                <RegistrationDetails>
                  <summary>Registration details</summary>
                  <RegistrationDetailsGrid>
                    <InfoItem>
                      <InfoLabel>HAPI ID</InfoLabel>
                      <ReadOnlyValue code value={String(attendee.userId)} />
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>T-shirt</InfoLabel>
                      <ReadOnlyValue
                        value={[
                          attendee.registrationData.shirtType,
                          attendee.registrationData.shirtSize,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      />
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Pronouns</InfoLabel>
                      <ReadOnlyValue
                        value={attendee.registrationData.pronouns}
                      />
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Shirt accommodations</InfoLabel>
                      <ReadOnlyValue
                        value={attendee.registrationData.shirtAccommodations}
                      />
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Accessibility needs</InfoLabel>
                      <ReadOnlyValue
                        value={attendee.registrationData.accessibilityNeeds}
                      />
                    </InfoItem>
                  </RegistrationDetailsGrid>
                </RegistrationDetails>

                <NetworkingDetails>
                  <summary>Networking info loaded from RSVP</summary>
                  <NetworkingGrid>
                    <InfoItem>
                      <InfoLabel>Badge email</InfoLabel>
                      <EditableValue
                        label="Badge email"
                        value={badgeDraft.networkingEmail}
                        savedValue={savedBadgeDraft.networkingEmail}
                        saveResult={fieldSaveResults.networkingEmail}
                        disabledReason={profileSaveDisabledReason}
                        onChange={(value) =>
                          updateBadgeDraft("networkingEmail", value)
                        }
                        onSave={() => void saveBadgeField("networkingEmail")}
                      />
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Phone</InfoLabel>
                      <EditableValue
                        label="Badge phone"
                        value={badgeDraft.networkingPhone}
                        savedValue={savedBadgeDraft.networkingPhone}
                        saveResult={fieldSaveResults.networkingPhone}
                        disabledReason={profileSaveDisabledReason}
                        onChange={(value) =>
                          updateBadgeDraft("networkingPhone", value)
                        }
                        onSave={() => void saveBadgeField("networkingPhone")}
                      />
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>LinkedIn</InfoLabel>
                      <EditableValue
                        label="LinkedIn"
                        value={badgeDraft.networkingLinkedIn}
                        savedValue={savedBadgeDraft.networkingLinkedIn}
                        saveResult={fieldSaveResults.networkingLinkedIn}
                        disabledReason={profileSaveDisabledReason}
                        onChange={(value) =>
                          updateBadgeDraft("networkingLinkedIn", value)
                        }
                        onSave={() => void saveBadgeField("networkingLinkedIn")}
                      />
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Discord</InfoLabel>
                      <EditableValue
                        label="Discord"
                        value={badgeDraft.networkingDiscord}
                        savedValue={savedBadgeDraft.networkingDiscord}
                        saveResult={fieldSaveResults.networkingDiscord}
                        disabledReason={profileSaveDisabledReason}
                        onChange={(value) =>
                          updateBadgeDraft("networkingDiscord", value)
                        }
                        onSave={() => void saveBadgeField("networkingDiscord")}
                      />
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Instagram</InfoLabel>
                      <EditableValue
                        label="Instagram"
                        value={badgeDraft.networkingInstagram}
                        savedValue={savedBadgeDraft.networkingInstagram}
                        saveResult={fieldSaveResults.networkingInstagram}
                        disabledReason={profileSaveDisabledReason}
                        onChange={(value) =>
                          updateBadgeDraft("networkingInstagram", value)
                        }
                        onSave={() =>
                          void saveBadgeField("networkingInstagram")
                        }
                      />
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>X</InfoLabel>
                      <EditableValue
                        label="X"
                        value={badgeDraft.networkingX}
                        savedValue={savedBadgeDraft.networkingX}
                        saveResult={fieldSaveResults.networkingX}
                        disabledReason={profileSaveDisabledReason}
                        onChange={(value) =>
                          updateBadgeDraft("networkingX", value)
                        }
                        onSave={() => void saveBadgeField("networkingX")}
                      />
                    </InfoItem>
                  </NetworkingGrid>
                </NetworkingDetails>

                {hasUnsavedBadgeChanges && (
                  <UnsavedChangesWarning role="status">
                    Save every changed field before checking this attendee in or
                    programming their badge.
                  </UnsavedChangesWarning>
                )}

                <BlockedActions
                  $blocked={registrationBlocked}
                  title={
                    registrationBlocked
                      ? "Registration is blocked. Send this attendee to Special Cases."
                      : undefined
                  }
                >
                  <ActionRow>
                    {receivesBadge && (
                      <Button
                        color={
                          connectionState === "connected"
                            ? "primary"
                            : "secondary"
                        }
                        size="md"
                        disabled={programDataUnavailable}
                        onClick={() => requestBadgeProgramming()}
                      >
                        {operation === "allocating"
                          ? "Allocating ID…"
                          : operation === "programming"
                          ? "Programming…"
                          : physicalActionLabel}
                      </Button>
                    )}

                    <Button
                      color={receivesBadge ? "secondary" : "primary"}
                      size="md"
                      disabled={
                        busy ||
                        registeredBadgeIsVirtual ||
                        registrationBlocked ||
                        hasUnsavedBadgeChanges
                      }
                      onClick={() => void checkInWithoutBadge()}
                    >
                      {operation === "allocating"
                        ? "Checking in…"
                        : registeredBadgeIsVirtual
                        ? "Virtual badge ID assigned"
                        : hasRegisteredPhysicalBadge
                        ? "Replace with virtual badge ID"
                        : "Check in without a physical badge"}
                    </Button>

                    {!receivesBadge && (
                      <Button
                        color="secondary"
                        size="md"
                        disabled={
                          busy || registrationBlocked || hasUnsavedBadgeChanges
                        }
                        onClick={() => {
                          setShowOverrideModal(true);
                        }}
                      >
                        {hasRegisteredPhysicalBadge
                          ? "Program their assigned badge"
                          : "Check them in with a badge"}
                      </Button>
                    )}

                    {isOrganizer && (
                      <OverrideRow>
                        {canForceConfirm && (
                          <Button
                            color="secondary-danger"
                            size="md"
                            data-organizer-override
                            disabled={busy || forceConfirming}
                            onClick={() => setShowForceConfirmModal(true)}
                          >
                            Move to confirmed
                          </Button>
                        )}
                        <Button
                          color="secondary-danger"
                          size="md"
                          data-organizer-override
                          disabled={
                            waiverStatus?.signed !== false ||
                            !stageIsEligible ||
                            !hasEventAttendeeRecord ||
                            busy ||
                            hasWaiverOverride
                          }
                          onClick={() => setShowWaiverOverrideModal(true)}
                        >
                          Override waiver requirement
                        </Button>
                      </OverrideRow>
                    )}
                  </ActionRow>
                </BlockedActions>
              </AttendeeSection>
            )}
          </Panel>

          <SideColumn>
            <Panel>
              <PanelHeading>
                <StepNumber>2</StepNumber>
                <div>
                  <Text type="h2">Badge station</Text>
                  <MutedText>Connect and verify badge hardware.</MutedText>
                </div>
              </PanelHeading>

              {status && !status.requiresSpecialCases && (
                <StatusBanner
                  tone={status.tone}
                  role="status"
                  aria-live="polite"
                >
                  <StatusCopy>
                    <StatusTitle>{status.title}</StatusTitle>
                    {status.detail && (
                      <StatusDetail>{status.detail}</StatusDetail>
                    )}
                  </StatusCopy>
                  {busy && <Spinner />}
                </StatusBanner>
              )}

              {!BadgeProvisioner.isSupported() ? (
                <InlineError>
                  Web Serial is unavailable. Open this page in Chrome or Edge on
                  desktop to program badges.
                </InlineError>
              ) : (
                <>
                  <ConnectionRow $hasError={badgeConnectionError}>
                    <ConnectionDot
                      $state={
                        badgeConnectionError
                          ? "error"
                          : connectionState === "connected"
                          ? "connected"
                          : "disconnected"
                      }
                    />
                    <strong>
                      {connectionState === "connected"
                        ? "Badge connected"
                        : connectionState === "connecting"
                        ? "Connecting…"
                        : "No badge connected"}
                    </strong>
                  </ConnectionRow>
                  {inspection && (
                    <ConnectedBadge>
                      <InfoLabel>
                        {inspection.provisioned
                          ? "This badge is associated to"
                          : "Connected hardware"}
                      </InfoLabel>
                      <InfoValue>
                        {inspection.provisioned
                          ? connectedBadgeAssociation
                          : "Unassigned badge"}
                      </InfoValue>
                      <CodeValue>{inspection.mac}</CodeValue>
                    </ConnectedBadge>
                  )}
                  <StationActions>
                    {connectionState === "connected" ? (
                      <>
                        <Button
                          color="secondary"
                          size="sm"
                          disabled={
                            busy ||
                            attendeeLoading ||
                            !inspection?.identity?.badge_id
                          }
                          onClick={lookupConnectedBadge}
                        >
                          {attendeeLoading ? "Looking up…" : "Lookup badge"}
                        </Button>
                        <Button
                          color="secondary"
                          size="sm"
                          disabled={busy}
                          onClick={() => void disconnectBadge()}
                        >
                          Disconnect
                        </Button>
                      </>
                    ) : (
                      <Button
                        color="primary"
                        size="md"
                        disabled={busy}
                        onClick={() => void connectBadge()}
                      >
                        Connect badge
                      </Button>
                    )}
                  </StationActions>
                  <ConnectionHelp>
                    Switch the badge off, connect a{" "}
                    <strong>USB-C data cable</strong>, then switch it on. Choose
                    the USB JTAG/serial debug unit when prompted.
                  </ConnectionHelp>
                </>
              )}

              <ConnectionHelp role="note">
                Before syncing, close any open game or app and return to the
                badge’s <strong>app launcher screen</strong>.
              </ConnectionHelp>

              <Divider />
              <FieldLabel htmlFor="badge-app-mode">
                Initial badge apps
              </FieldLabel>
              <Select
                id="badge-app-mode"
                fullWidth
                value={appMode}
                disabled={busy}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                  setAppMode(event.target.value as BadgeAppMode)
                }
              >
                {(Object.keys(APP_MODE_LABELS) as BadgeAppMode[]).map(
                  (mode) => (
                    <Select.Option key={mode} value={mode}>
                      {APP_MODE_LABELS[mode]}
                    </Select.Option>
                  )
                )}
              </Select>
              <ConnectionHelp>
                Hackers start with default apps. Non-hackers start with all
                public apps. Organizers also get diagnostic tools such as Test
                and Lanyards.
              </ConnectionHelp>

              {connectionState === "connected" && (
                <DangerZone>
                  <Button
                    color="secondary-danger"
                    size="sm"
                    disabled={busy}
                    onClick={() => void factoryResetBadge()}
                  >
                    Factory-reset badge
                  </Button>
                  <FactoryResetHelp>
                    After a factory reset, unplug the badge and plug it back in.
                  </FactoryResetHelp>
                </DangerZone>
              )}
            </Panel>

            <LogDetails>
              <summary>Serial log</summary>
              <SerialLog>{serialLog || "No badge activity yet."}</SerialLog>
              {serialLog && (
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => setSerialLog("")}
                >
                  Clear log
                </Button>
              )}
            </LogDetails>
          </SideColumn>
        </Workspace>
      </CompactPageWrapper>

      <Modal
        open={showOverrideModal}
        onClose={() => setShowOverrideModal(false)}
      >
        <Modal.Header>Give a hacker badge anyway?</Modal.Header>
        <Modal.Body>
          Hey, {attendeeName} is part of the <strong>{attendee?.role}</strong>{" "}
          group, which usually should not get a hacker badge. Just confirming
          that you actually do want to give them a badge.
        </Modal.Body>
        <Modal.Footer justify="end">
          <Button color="secondary" onClick={() => setShowOverrideModal(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={programDataUnavailable}
            onClick={() => {
              setShowOverrideModal(false);
              requestBadgeProgramming();
            }}
          >
            Yes, check them in with a badge
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={isOrganizer && showForceConfirmModal}
        onClose={() => {
          if (forceConfirming) return;
          setShowForceConfirmModal(false);
        }}
      >
        <Modal.Header>Move to confirmed?</Modal.Header>
        <Modal.Body>
          <p>
            This will set <strong>{attendeeName}</strong>&apos;s application
            claim to confirmed, regardless of the current stage (
            {attendee?.claimStage || "unknown"}).
          </p>
          <p>
            Only continue if you intend to skip or override RSVP. This updates
            their claim in HAPI and may send the usual confirmation email.
          </p>
        </Modal.Body>
        <Modal.Footer justify="end">
          <Button
            color="secondary"
            disabled={forceConfirming}
            onClick={() => setShowForceConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={forceConfirming}
            onClick={() => {
              forceConfirmAcceptedClaim();
            }}
          >
            {forceConfirming ? "Confirming…" : "Confirm application"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={isOrganizer && showWaiverOverrideModal}
        onClose={() => setShowWaiverOverrideModal(false)}
      >
        <Modal.Header>Override unsigned waiver?</Modal.Header>
        <Modal.Body>
          <p>
            <strong>{attendeeName}</strong> does not have a completed waiver in
            HAPI. Only continue if you have independently confirmed that
            registration may proceed.
          </p>
          <p>
            This acknowledgement applies only to this registration attempt and
            does not change or mark the waiver as signed.
          </p>
        </Modal.Body>
        <Modal.Footer justify="end">
          <Button
            color="secondary"
            onClick={() => setShowWaiverOverrideModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              if (!isOrganizer) return;
              setWaiverOverrideAcknowledged(true);
              setShowWaiverOverrideModal(false);
            }}
          >
            Continue without completed waiver
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={showAlreadyCheckedInModal}
        onClose={() => setShowAlreadyCheckedInModal(false)}
      >
        <Modal.Header>Attendee already checked in</Modal.Header>
        <Modal.Body>
          <strong>{attendeeName}</strong> already has a checked-in claim. Only
          continue if you intend to program or replace their physical badge.
        </Modal.Body>
        <Modal.Footer justify="end">
          <Button
            color="secondary"
            onClick={() => setShowAlreadyCheckedInModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setShowAlreadyCheckedInModal(false);
              requestBadgeProgramming(true);
            }}
          >
            Confirm &amp; continue
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={showNoBadgeModal} onClose={() => setShowNoBadgeModal(false)}>
        <Modal.Header>Connect a badge first</Modal.Header>
        <Modal.Body>
          The badge programming action is unavailable because no badge is
          connected. Connect the badge with a USB-C data cable, switch it on,
          and choose its serial port before trying again.
        </Modal.Body>
        <Modal.Footer justify="end">
          <Button color="secondary" onClick={() => setShowNoBadgeModal(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setShowNoBadgeModal(false);
              void connectBadge();
            }}
          >
            Connect badge
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={showBadgeOverwriteModal}
        onClose={() => setShowBadgeOverwriteModal(false)}
      >
        <Modal.Header>Overwrite this badge?</Modal.Header>
        <Modal.Body>
          This badge is currently assigned to{" "}
          <strong>{badgeToOverwriteId}</strong>. Programming it for{" "}
          <strong>{attendeeName}</strong> will replace the identity and selected
          app mode. Confirm that this is the physical badge you intend to
          reassign.
        </Modal.Body>
        <Modal.Footer justify="end">
          <Button
            color="secondary"
            onClick={() => setShowBadgeOverwriteModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={programDataUnavailable}
            onClick={() => {
              setShowBadgeOverwriteModal(false);
              void checkInAndProgramBadge({ allowOverwrite: true });
            }}
          >
            Confirm overwrite &amp; program
          </Button>
        </Modal.Footer>
      </Modal>

      {activeError && (
        <BottomErrorToast role="alert" aria-live="assertive">
          <ErrorToastContent>
            <StatusCopy>
              <StatusTitle>Something went wrong</StatusTitle>
              <StatusDetail>
                Send this attendee to Special Cases for help.
              </StatusDetail>
            </StatusCopy>
            {showActiveErrorDetails && (
              <ErrorDetails role="status">
                <strong>{activeError.title}</strong>
                {activeError.detail && <span>{activeError.detail}</span>}
              </ErrorDetails>
            )}
          </ErrorToastContent>
          <ErrorToastActions>
            <Button
              color="secondary"
              size="sm"
              aria-expanded={showActiveErrorDetails}
              onClick={() => setShowActiveErrorDetails((visible) => !visible)}
            >
              {showActiveErrorDetails ? "Hide details" : "See details"}
            </Button>
            <Button color="secondary" size="sm" onClick={dismissActiveError}>
              Dismiss
            </Button>
          </ErrorToastActions>
        </BottomErrorToast>
      )}
    </>
  );
};

const disconnectedPulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(0.82);
  }
`;

const CompactPageWrapper = styled(PageWrapper)`
  padding: 20px 32px;

  ${mediaQueries.tablet} {
    padding: 20px;
  }

  ${mediaQueries.largeMobile} {
    padding: 16px;
  }
`;

const Workspace = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(340px, 0.75fr);
  gap: 16px;
  align-items: start;

  ${mediaQueries.tablet} {
    grid-template-columns: 1fr;
  }
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: sticky;
  top: 16px;

  ${mediaQueries.tablet} {
    position: static;
  }
`;

const Panel = styled.section`
  background: ${({ theme }) => theme.globalConstants.color.white};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 12px;
  padding: 18px;
`;

const DesktopOnlyPanel = styled(Panel)`
  max-width: 560px;
  margin: 48px auto;
  padding: 32px;
  text-align: center;
`;

const PanelHeading = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const HeadingCopy = styled.div`
  min-width: 0;
  flex: 1;
`;

const StepNumber = styled.span`
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${({ theme }) => theme.globalConstants.color.textLight};
  background: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  font-weight: 700;
`;

const MutedText = styled.p`
  margin-top: 4px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const FastModeSwitch = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 5px 8px;
  border: 0;
  border-radius: 7px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  background: transparent;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  }

  ${mediaQueries.largeMobile} {
    span:last-child {
      white-space: nowrap;
    }
  }
`;

const SwitchTrack = styled.span<{ $enabled: boolean }>`
  position: relative;
  width: 34px;
  height: 20px;
  flex: 0 0 34px;
  border-radius: 999px;
  background: ${({ theme, $enabled }) =>
    $enabled
      ? theme.globalConstants.color.backgroundPrimary
      : theme.globalConstants.color.backgroundGrayDark};
  transition: background 150ms ease;
`;

const SwitchThumb = styled.span<{ $enabled: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ $enabled }) => ($enabled ? "17px" : "3px")};
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 3px rgba(31, 41, 55, 0.3);
  transition: left 150ms ease;
`;

const StatusBanner = styled.div<{ tone: StatusTone }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid
    ${({ theme, tone }) =>
      tone === "success"
        ? theme.globalConstants.color.borderSuccess
        : tone === "warning" || tone === "busy"
        ? theme.globalConstants.color.borderWarning
        : tone === "error"
        ? theme.globalConstants.color.borderDanger
        : theme.globalConstants.color.borderGray};
  background: ${({ theme, tone }) =>
    tone === "success"
      ? theme.globalConstants.color.backgroundSuccessLight
      : tone === "warning" || tone === "busy"
      ? theme.globalConstants.color.backgroundWarningLight
      : tone === "error"
      ? theme.globalConstants.color.backgroundDangerLight
      : theme.globalConstants.color.backgroundLight};
`;

const AttendeeStatusBanner = styled(StatusBanner)`
  margin-bottom: 12px;
`;

const BottomErrorToast = styled.div`
  position: fixed;
  z-index: 1000;
  left: 50%;
  bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: calc(100% - 32px);
  max-width: 620px;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderDanger};
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundDangerLight};
  box-shadow: 0 8px 24px rgba(31, 41, 55, 0.2);
  transform: translateX(-50%);

  ${mediaQueries.largeMobile} {
    bottom: 64px;
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ErrorToastContent = styled.div`
  min-width: 0;
`;

const ErrorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid
    ${({ theme }) => theme.globalConstants.color.borderDanger};
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 13px;
  overflow-wrap: anywhere;
`;

const ErrorToastActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: 8px;

  ${mediaQueries.largeMobile} {
    width: 100%;
  }
`;

const StatusCopy = styled.div`
  min-width: 0;
`;

const StatusTitle = styled.p`
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

const StatusDetail = styled.p`
  margin-top: 2px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  overflow-wrap: anywhere;
`;

const LookupModeToggle = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
  }
`;

const FullWidthInput = styled(TextInput)`
  flex: 1;
  width: 100%;
  min-height: 46px;
  font-size: 18px;
`;

const SearchButton = styled(Button)`
  flex: 0 0 auto;
  min-width: 132px;
  min-height: 46px;
  white-space: nowrap;

  ${mediaQueries.largeMobile} {
    width: 100%;
  }
`;

const FastModeHint = styled.p`
  margin-top: 6px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 12px;
`;

const AttendeeSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const AttendeeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;

  ${mediaQueries.largeMobile} {
    flex-direction: column;
  }
`;

const AttendeeName = styled.h3`
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 24px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

const RoleLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const RolePill = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimaryLight};
  color: ${({ theme }) => theme.globalConstants.color.black};
  font-weight: 700;
  text-transform: capitalize;
`;

const WaiverPill = styled.span<{ $signed: boolean; $unknown: boolean }>`
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ theme, $signed, $unknown }) =>
    $unknown
      ? theme.globalConstants.color.backgroundGray
      : $signed
      ? theme.globalConstants.color.backgroundSuccessLight
      : theme.globalConstants.color.backgroundDangerLight};
  color: ${({ theme }) => theme.globalConstants.color.black};
  font-size: 14px;
  font-weight: 700;
`;

const StagePill = styled.span`
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  color: ${({ theme }) => theme.globalConstants.color.black};
  font-size: 14px;
  font-weight: 700;
  text-transform: capitalize;
`;

const RegistrationStatusPill = styled.span<{
  $tone: "neutral" | "success" | "warning" | "danger";
}>`
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ theme, $tone }) =>
    $tone === "success"
      ? theme.globalConstants.color.backgroundSuccessLight
      : $tone === "warning"
      ? theme.globalConstants.color.backgroundWarningLight
      : $tone === "danger"
      ? theme.globalConstants.color.backgroundDangerLight
      : theme.globalConstants.color.backgroundGray};
  color: ${({ theme }) => theme.globalConstants.color.black};
  font-size: 14px;
  font-weight: 700;
`;

const RegistrationBlockWarning = styled.div`
  margin-bottom: 12px;
  padding: 14px;
  border-radius: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundDangerLight};

  ul {
    margin: 8px 0 8px 20px;
  }
`;

const AlreadyCheckedInWarning = styled.div`
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundWarningLight};
`;

const RoleNotice = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textWarning};
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundWarningLight};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;

  ${mediaQueries.mobile} {
    grid-template-columns: 1fr;
  }
`;

const NetworkingGrid = styled(InfoGrid)`
  width: 100%;
  min-width: 0;
  margin-top: 12px;
`;

const InfoItem = styled.div`
  min-width: 0;
`;

const InfoLabel = styled.p`
  margin-bottom: 4px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const InfoValue = styled.p`
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  overflow-wrap: anywhere;
`;

const EditableFieldRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 8px;
`;

const EditableSaveButton = styled(Button)`
  flex: 0 0 82px;
  width: 82px;
  min-width: 82px;
  height: 36px;
  min-height: 36px;
  padding: 0 10px;
  white-space: nowrap;
`;

const EditableInput = styled.input<{ $code: boolean }>`
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  min-height: 36px;
  padding: 7px 9px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 7px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  background: ${({ theme }) => theme.globalConstants.color.white};
  font: inherit;
  font-family: ${({ theme, $code }) =>
    $code
      ? theme.globalConstants.fontFamily.code
      : theme.globalConstants.fontFamily.body};

  &:focus {
    border-color: ${({ theme }) => theme.globalConstants.color.borderFocus};
    outline: 2px solid
      ${({ theme }) => theme.globalConstants.color.borderFocusLight};
  }

  &:disabled {
    color: ${({ theme }) => theme.globalConstants.color.textSecondary};
    background: ${({ theme }) => theme.globalConstants.color.backgroundLight};
    cursor: not-allowed;
  }
`;

const FieldSaveError = styled.p`
  margin-top: 4px;
  color: ${({ theme }) => theme.globalConstants.color.textDanger};
  font-size: 12px;
`;

const ReadOnlyField = styled.p<{ $code: boolean }>`
  min-height: 36px;
  padding: 8px 10px;
  border-radius: 7px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  background: ${({ theme }) => theme.globalConstants.color.backgroundLight};
  font-family: ${({ theme, $code }) =>
    $code
      ? theme.globalConstants.fontFamily.code
      : theme.globalConstants.fontFamily.body};
  overflow-wrap: anywhere;
`;

const CodeValue = styled(InfoValue)`
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.code};
`;

const NetworkingDetails = styled.details`
  width: 100%;
  min-width: 0;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};

  summary {
    cursor: pointer;
    color: ${({ theme }) => theme.globalConstants.color.textSecondary};
    font-weight: 700;
  }
`;

const RegistrationDetails = styled(NetworkingDetails)`
  margin-top: 12px;
`;

const RegistrationDetailsGrid = styled(InfoGrid)`
  margin-top: 10px;
  row-gap: 8px;

  ${ReadOnlyField} {
    min-height: 32px;
    padding: 6px 8px;
  }
`;

const UnsavedChangesWarning = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textWarning};
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundWarningLight};
  font-weight: 600;
`;

const InlineError = styled.div`
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textDanger};
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundDangerLight};

  button {
    margin-top: 12px;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
`;

const OverrideRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  align-items: center;
`;

const BlockedActions = styled.div<{ $blocked: boolean }>`
  cursor: ${({ $blocked }) => ($blocked ? "not-allowed" : "auto")};

  ${({ $blocked }) =>
    $blocked
      ? `
    button:not([data-organizer-override]) {
      pointer-events: none;
    }
  `
      : ""}
`;

const ConnectionRow = styled.div<{ $hasError: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError
        ? theme.globalConstants.color.borderDanger
        : theme.globalConstants.color.borderGray};
  border-radius: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  background: ${({ theme, $hasError }) =>
    $hasError
      ? theme.globalConstants.color.backgroundDangerLight
      : theme.globalConstants.color.white};
`;

const ConnectionDot = styled.span<{
  $state: "connected" | "disconnected" | "error";
}>`
  width: 10px;
  height: 10px;
  flex: 0 0 10px;
  border-radius: 50%;
  background: ${({ theme, $state }) =>
    $state === "connected"
      ? theme.globalConstants.color.backgroundSuccess
      : theme.globalConstants.color.backgroundDanger};
  animation: ${({ $state }) =>
      $state === "disconnected" ? disconnectedPulse : "none"}
    2.4s ease-in-out infinite;
`;

const ConnectedBadge = styled.div`
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundLight};
`;

const StationActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const ConnectionHelp = styled.p`
  margin-top: 12px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 13px;
  line-height: 1.5;
`;

const Divider = styled.hr`
  margin: 16px 0;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-weight: 700;
`;

const DangerZone = styled.div`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const FactoryResetHelp = styled.p`
  margin-top: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 12px;
`;

const LogDetails = styled.details`
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 10px;
  background: ${({ theme }) => theme.globalConstants.color.white};

  summary {
    cursor: pointer;
    color: ${({ theme }) => theme.globalConstants.color.textSecondary};
    font-weight: 700;
  }
`;

const SerialLog = styled.pre`
  max-height: 220px;
  overflow: auto;
  margin: 12px 0;
  padding: 12px;
  border-radius: 6px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  background: ${({ theme }) => theme.globalConstants.color.backgroundDark};
  font-size: 11px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;
